import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { format, addDays } from 'date-fns';
import { Loader2, Calendar, Clock, CheckCircle2, MapPin, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Button } from '@/components/atoms/button';
import { CancelAppointmentDialog } from '@/components/public/CancelAppointmentDialog';
import { cn } from '@/lib/utils';
import {
  dateStringToDate,
  dateKeyInTimeZone,
  formatDateTimeInTimeZone,
  formatWhenInTimeZone,
  formatTimeZoneLabel,
} from '@/lib/date-values';
import {
  useScheduleRequestContext,
  useScheduleAvailability,
  useBookSlot,
  useConfirmReservedAppointment,
  useRescheduleSlot,
  useRequestScheduleReschedule,
  useCancelBooking,
} from '@/hooks/public/useScheduleRequest';
import { RescheduleRequestForm } from '@/components/public/confirm/RescheduleRequestForm';
import type {
  AvailabilitySlot,
  PublicBookingAddress,
  ScheduleRequestContext,
} from '@/types/scheduleRequest';
import { getBookingContactActionLabel, getBookingContactHref } from '@/lib/public-booking-contact';

/** One chip per start time — the customer never picks a technician (see the
 * online-booking wizard's dedupeByStart); the server re-validates on confirm. */
function dedupeByStartAt<T extends { startAt: string }>(slots: T[]): T[] {
  const seen = new Set<string>();
  return slots.filter((s) => {
    if (seen.has(s.startAt)) return false;
    seen.add(s.startAt);
    return true;
  });
}

function slotsMatchingStartAt(
  slots: AvailabilitySlot[] | undefined,
  selected: AvailabilitySlot,
): AvailabilitySlot[] {
  return slots?.filter((candidate) => candidate.startAt === selected.startAt) ?? [];
}

function orderBySelectedSlot(slots: AvailabilitySlot[], selected: AvailabilitySlot) {
  return [...slots].sort((a, b) => {
    if (a.slotId === selected.slotId) return -1;
    if (b.slotId === selected.slotId) return 1;
    return 0;
  });
}

export const Route = createFileRoute('/(public)/book/$token')({
  component: BookPage,
  validateSearch: z.object({ reschedule: z.boolean().optional() }),
});

const RANGE_DAYS = 14;

/** Short time in a tz, e.g. "4:30 PM". */
function timeInTz(iso: string, tz: string | null | undefined): string {
  return formatDateTimeInTimeZone(iso, tz, { hour: 'numeric', minute: '2-digit' });
}

function BookPage() {
  const { token } = Route.useParams();
  const { reschedule: startReschedule } = Route.useSearch();
  const { data, isLoading, isError } = useScheduleRequestContext(token);

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </PublicLayout>
    );
  }

  if (isError || !data) {
    return (
      <Terminal title="Link unavailable" body="This booking link is invalid or has expired." />
    );
  }

  if (data.status === 'EXPIRED' || data.status === 'REVOKED') {
    return (
      <Terminal
        title="Link no longer active"
        body="This booking link has expired or been withdrawn. Please contact us to schedule."
        brand={data}
      />
    );
  }

  if (data.status === 'CANCELLED') {
    return (
      <Terminal
        title="Appointment cancelled"
        body="This appointment was cancelled. Contact us if you'd like to rebook."
        brand={data}
      />
    );
  }

  if (data.status === 'BOOKED' || data.status === 'RESCHEDULED') {
    return <BookedView token={token} ctx={data} startReschedule={startReschedule} />;
  }

  if (data.requiresConfirmation && data.appointment) {
    return <ConfirmReservedView token={token} ctx={data} />;
  }

  // SENT / VIEWED without a reserved visit → pick a time.
  return <SlotPicker token={token} ctx={data} mode="book" />;
}

function ConfirmReservedView({ token, ctx }: { token: string; ctx: ScheduleRequestContext }) {
  const confirm = useConfirmReservedAppointment(token);
  const appt = ctx.appointment;
  const when = appt?.startAt
    ? formatWhenInTimeZone(appt.startAt, appt.timezone)
    : 'your reserved appointment';
  const apptRange =
    appt?.startAt && appt.endAt ? formatRange(appt.startAt, appt.endAt, appt.timezone) : null;
  const arrivalRange = appt?.arrivalWindow
    ? formatRange(appt.arrivalWindow.startAt, appt.arrivalWindow.endAt, appt.timezone)
    : null;

  return (
    <PublicLayout maxWidth="sm">
      <Card>
        <Brand ctx={ctx} />

        <div className="flex items-center gap-2.5">
          <Calendar className="h-7 w-7 shrink-0 text-indigo-600" />
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">
            Confirm your appointment
          </h1>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          This time is reserved for you. Confirm it below to let us know you&apos;ll be there.
        </p>

        <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50/70 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {ctx.serviceName}
          </p>
          <p className="mt-1.5 text-lg font-semibold text-gray-900">{when}</p>
          {arrivalRange || apptRange ? (
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-600">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              {arrivalRange ?? apptRange}
            </p>
          ) : null}
          {appt?.technicianNames?.length || appt?.technicianName ? (
            <p className="mt-1 text-sm text-gray-600">
              with{' '}
              {appt.technicianNames?.length ? appt.technicianNames.join(', ') : appt.technicianName}
            </p>
          ) : null}
        </div>

        <p className="mt-4 flex items-start gap-1.5 text-sm text-gray-600">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
          {ctx.address}
        </p>
        {appt?.timezone ? (
          <p className="mt-1 pl-5 text-xs text-gray-400">
            Times shown in{' '}
            {formatTimeZoneLabel(appt.timezone, appt.startAt ? new Date(appt.startAt) : undefined)}
          </p>
        ) : null}

        <Button
          className="mt-6 w-full"
          disabled={confirm.isPending}
          onClick={() =>
            confirm.mutate(undefined, {
              onError: (error) =>
                toast.error(
                  error instanceof Error ? error.message : 'Could not confirm appointment',
                ),
            })
          }
        >
          {confirm.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Confirm appointment
        </Button>
      </Card>
    </PublicLayout>
  );
}

// ─── Booked confirmation + manage ─────────────────────────────────────────────

function BookedView({
  token,
  ctx,
  startReschedule = false,
}: {
  token: string;
  ctx: ScheduleRequestContext;
  startReschedule?: boolean;
}) {
  const [rescheduling, setRescheduling] = useState(
    startReschedule && ctx.canReschedule && ctx.rescheduleMode === 'SELF_SERVICE',
  );
  const [requestingReschedule, setRequestingReschedule] = useState(
    startReschedule && ctx.canReschedule && ctx.rescheduleMode === 'REQUEST',
  );
  const cancel = useCancelBooking(token);
  const requestReschedule = useRequestScheduleReschedule(token);

  if (rescheduling) {
    return (
      <SlotPicker
        token={token}
        ctx={ctx}
        mode="reschedule"
        onCancel={() => setRescheduling(false)}
        onDone={() => setRescheduling(false)}
      />
    );
  }

  if (requestingReschedule) {
    return (
      <PublicLayout maxWidth="sm">
        <Card>
          <Brand ctx={ctx} />
          <RescheduleRequestForm
            isSubmitting={requestReschedule.isPending}
            onCancel={() => setRequestingReschedule(false)}
            onSubmit={(input) =>
              requestReschedule.mutate(input, {
                onSuccess: () => {
                  toast.success('Your reschedule request was sent to dispatch.');
                  setRequestingReschedule(false);
                },
                onError: (error) =>
                  toast.error(
                    error instanceof Error ? error.message : 'Could not send your request',
                  ),
              })
            }
          />
        </Card>
      </PublicLayout>
    );
  }

  const appt = ctx.appointment;
  const when = appt?.startAt
    ? formatWhenInTimeZone(appt.startAt, appt.timezone)
    : 'your selected time';
  const apptRange =
    appt?.startAt && appt.endAt ? formatRange(appt.startAt, appt.endAt, appt.timezone) : null;
  const arrivalRange = appt?.arrivalWindow
    ? formatRange(appt.arrivalWindow.startAt, appt.arrivalWindow.endAt, appt.timezone)
    : null;
  const contactHref = getBookingContactHref(ctx.branding);
  const contactActionLabel = getBookingContactActionLabel(contactHref);
  const heading =
    appt?.status === 'COMPLETED'
      ? 'Appointment completed'
      : appt?.status === 'IN_PROGRESS'
        ? 'Visit in progress'
        : appt?.status === 'CANCELLATION_PENDING'
          ? 'Cancellation requested'
          : "You're booked!";

  return (
    <PublicLayout maxWidth="sm">
      <Card>
        <Brand ctx={ctx} />

        <div className="flex items-center gap-2.5 text-emerald-600">
          <CheckCircle2 className="h-7 w-7 shrink-0" />
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">{heading}</h1>
        </div>

        <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50/70 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {ctx.serviceName}
          </p>
          <p className="mt-1.5 text-lg font-semibold text-gray-900">{when}</p>
          {(arrivalRange || apptRange) && (
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-600">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              {arrivalRange ?? apptRange}
              <span className="text-gray-400">
                · {arrivalRange ? 'arrival window' : `${formatDuration(ctx.durationMinutes)} visit`}
              </span>
            </p>
          )}
          {(appt?.technicianNames.length || appt?.technicianName) && (
            <p className="mt-1 text-sm text-gray-600">
              with{' '}
              {appt.technicianNames.length ? appt.technicianNames.join(', ') : appt.technicianName}
            </p>
          )}
        </div>

        <p className="mt-4 flex items-start gap-1.5 text-sm text-gray-600">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
          {ctx.address}
        </p>
        {appt?.timezone ? (
          <p className="mt-1 pl-5 text-xs text-gray-400">
            Times shown in {formatTimeZoneLabel(appt.timezone)}
          </p>
        ) : null}

        {ctx.scheduleChanged && !ctx.rescheduleRequested && <p className="mt-5 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">Your previous appointment time has been cancelled. This is your updated schedule.</p>}
        {ctx.rescheduleRequested ? (
          <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <p className="font-medium">Reschedule requested</p>
            <p className="mt-0.5 text-blue-700">
              Dispatch has been notified. Your current time stays reserved until they move it.
            </p>
          </div>
        ) : null}

        {appt?.status === 'CANCELLATION_PENDING' ? (
          <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <p className="font-medium">Cancellation pending</p>
            <p className="mt-0.5 text-blue-700">
              The office has your request and will confirm the cancellation with you.
            </p>
          </div>
        ) : null}

        {!ctx.rescheduleRequested && (ctx.canReschedule || ctx.canCancel) ? (
          <div className="mt-6 flex flex-wrap gap-2">
            {ctx.canReschedule && (
              <Button
                variant="outline"
                onClick={() =>
                  ctx.rescheduleMode === 'SELF_SERVICE'
                    ? setRescheduling(true)
                    : setRequestingReschedule(true)
                }
              >
                {ctx.rescheduleMode === 'SELF_SERVICE' ? 'Choose a new time' : 'Request reschedule'}
              </Button>
            )}
            {ctx.canCancel && (
              <CancelAppointmentDialog
                tenantName={ctx.branding.tenantName}
                when={
                  appt?.startAt
                    ? formatWhenInTimeZone(appt.startAt, appt.timezone, { withTime: false })
                    : null
                }
                isPending={cancel.isPending}
                onConfirm={() =>
                  cancel.mutate(undefined, {
                    onError: (e) =>
                      toast.error(e instanceof Error ? e.message : 'Could not cancel'),
                    onSuccess: () => toast.success('Appointment cancelled'),
                  })
                }
              />
            )}
          </div>
        ) : (
          <p className="mt-6 text-sm text-gray-500">
            Need to make a change?{' '}
            {contactHref ? (
              <a className="font-medium text-indigo-600 underline" href={contactHref}>
                Contact us.
              </a>
            ) : (
              'Contact us.'
            )}
          </p>
        )}
        {ctx.rescheduleMode === 'SELF_SERVICE' && ctx.maxCustomerReschedules > 0 ? (
          <p className="mt-3 text-xs text-gray-400">
            {ctx.reschedulesRemaining} of {ctx.maxCustomerReschedules} self-service{' '}
            {ctx.maxCustomerReschedules === 1 ? 'move' : 'moves'} remaining · changes close{' '}
            {ctx.policy.rescheduleCutoffHours} hours before the visit
          </p>
        ) : null}
      </Card>
    </PublicLayout>
  );
}

// ─── Slot picker (book or reschedule) ─────────────────────────────────────────

function SlotPicker({
  token,
  ctx,
  mode,
  onCancel,
  onDone,
}: {
  token: string;
  ctx: ScheduleRequestContext;
  mode: 'book' | 'reschedule';
  onCancel?: () => void;
  onDone?: () => void;
}) {
  const [dateFrom, dateTo] = useMemo(() => {
    const now = new Date();
    return [format(now, 'yyyy-MM-dd'), format(addDays(now, RANGE_DAYS), 'yyyy-MM-dd')];
  }, []);

  // Service-address choice (feature: book at a different address on file). Only
  // offered for a fresh booking — reschedule keeps the original address.
  const addressOptions = useMemo(() => buildAddressOptions(ctx), [ctx]);
  const showAddressPicker = mode === 'book' && addressOptions.length > 1;
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    addressOptions[0]?.id ?? null,
  );

  const availabilityQuery = useScheduleAvailability(
    token,
    dateFrom,
    dateTo,
    true,
    selectedAddressId ?? undefined,
  );
  const { data, isLoading, isError } = availabilityQuery;

  const currentStart = mode === 'reschedule' ? (ctx.appointment?.startAt ?? null) : null;
  const currentDay = currentStart ? currentStart.slice(0, 10) : null;

  const [selectedDay, setSelectedDay] = useState<string | null>(currentDay);
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);
  // Tracks whether the user has explicitly picked a slot yet (so we can show the
  // current appointment's slot as pre-highlighted until they choose another).
  const [touched, setTouched] = useState(false);

  const book = useBookSlot(token);
  const reschedule = useRescheduleSlot(token);
  const mutation = mode === 'book' ? book : reschedule;

  const byDay = useMemo(() => groupByDay(data?.slots ?? []), [data?.slots]);
  const days = Object.keys(byDay).sort();
  const activeDay = selectedDay ?? days[0] ?? null;
  // The business/job tz the slots are anchored to — label it so a traveling
  // customer knows which zone they're picking in (never the browser tz).
  const bookingTz = data?.timezone ?? ctx.appointment?.timezone ?? null;

  // Slot in the availability list that matches the current appointment time.
  const currentSlot = useMemo(
    () =>
      currentStart ? ((data?.slots ?? []).find((s) => s.startAt === currentStart) ?? null) : null,
    [data?.slots, currentStart],
  );

  // Effective selection: an explicit pick, or the current slot pre-highlighted.
  const activeSlot = touched ? selectedSlot : (selectedSlot ?? currentSlot);

  return (
    <PublicLayout maxWidth="sm">
      <Card>
        <Brand ctx={ctx} />

        <h1 className="text-xl font-semibold tracking-tight text-gray-900">
          {mode === 'book' ? 'Pick a time' : 'Choose a new time'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">{ctx.serviceName}</p>
        {showAddressPicker ? (
          <div className="mt-3">
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
              <MapPin className="h-3.5 w-3.5" />
              Service address
            </p>
            <div className="space-y-1.5">
              {addressOptions.map((opt) => {
                const isActive = opt.id === selectedAddressId;
                return (
                  <button
                    key={opt.id ?? 'default'}
                    type="button"
                    onClick={() => {
                      setSelectedAddressId(opt.id);
                      // Availability is address-specific; drop any pending pick
                      // and the chosen day (it may have no slots at the new
                      // address) so we fall back to the first available day.
                      setSelectedDay(null);
                      setSelectedSlot(null);
                      setTouched(true);
                    }}
                    className={cn(
                      'flex w-full items-start gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-colors',
                      isActive
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                        isActive ? 'bg-indigo-500' : 'bg-gray-300',
                      )}
                    />
                    <span>
                      {opt.label && (
                        <span className="mr-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                          {opt.label}
                        </span>
                      )}
                      {opt.line}
                      {opt.zip ? ` ${opt.zip}` : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="mt-1 flex items-start gap-1.5 text-sm text-gray-500">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            {ctx.address}
          </p>
        )}
        {bookingTz ? (
          <p className="mt-1 text-xs text-gray-400">
            Times shown in {formatTimeZoneLabel(bookingTz)}
          </p>
        ) : null}

        {mode === 'reschedule' && ctx.appointment?.startAt && (
          <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50/70 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-amber-700">
              Currently scheduled
            </p>
            <p className="mt-1 text-sm font-medium text-amber-900">
              {formatWhenInTimeZone(ctx.appointment.startAt, ctx.appointment.timezone, {
                month: 'short',
              })}
              {ctx.appointment.technicianName && ` with ${ctx.appointment.technicianName}`}
            </p>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : isError ? (
          // The availability request failed (vs. a successful-but-empty response).
          // Don't claim "no open times" — that conflates an error with real
          // emptiness. The status guards above already handle inactive links.
          <p className="py-10 text-center text-sm text-gray-500">
            We couldn&apos;t load available times right now. Please contact us to schedule.
          </p>
        ) : days.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">
            No open times in the next {RANGE_DAYS} days. Please contact us to schedule.
          </p>
        ) : (
          <div className="mt-5 space-y-4">
            {/* Step 1 — day */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                1 · Choose a day
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {days.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      setSelectedDay(day);
                      setSelectedSlot(null);
                      setTouched(true);
                    }}
                    className={cn(
                      'shrink-0 rounded-xl border px-3 py-2 text-left text-sm transition-colors',
                      day === activeDay
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50',
                    )}
                  >
                    <div className="flex items-center gap-1.5 font-medium">
                      <Calendar className="h-3.5 w-3.5" />
                      {format(dateStringToDate(day)!, 'EEE, MMM d')}
                    </div>
                    <div className="text-xs text-gray-500">
                      {byDay[day].length} time{byDay[day].length === 1 ? '' : 's'}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2 — time */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                2 · Choose a time
                <span className="ml-1 font-normal normal-case text-gray-400">
                  ({formatDuration(ctx.durationMinutes)} window)
                </span>
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {dedupeByStartAt(activeDay ? (byDay[activeDay] ?? []) : []).map((slot) => {
                  const isSelected = slot.slotId === activeSlot?.slotId;
                  const isCurrent = slot.startAt === currentStart;
                  return (
                    <button
                      key={slot.slotId}
                      type="button"
                      onClick={() => {
                        setSelectedSlot(slot);
                        setTouched(true);
                      }}
                      className={cn(
                        'flex flex-col items-center justify-center rounded-xl border px-2 py-2 text-sm transition-colors',
                        isSelected
                          ? 'border-indigo-600 bg-indigo-600 text-white shadow-sm'
                          : 'border-gray-200 text-gray-800 hover:border-indigo-300 hover:bg-indigo-50',
                      )}
                    >
                      <span className="flex items-center gap-1.5 font-medium">
                        <Clock className="h-3.5 w-3.5" />
                        {timeInTz(slot.startAt, slot.timezone)}
                      </span>
                      <span
                        className={cn(
                          'text-[11px]',
                          isSelected ? 'text-indigo-100' : 'text-gray-400',
                        )}
                      >
                        {isCurrent ? 'current time' : `– ${timeInTz(slot.endAt, slot.timezone)}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
          {onCancel && (
            <Button variant="ghost" onClick={onCancel} className="sm:mr-auto">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          <Button
            className="w-full sm:w-auto"
            disabled={!activeSlot || mutation.isPending}
            onClick={async () => {
              if (!activeSlot) return;
              const refreshed = await availabilityQuery.refetch();
              const candidates = orderBySelectedSlot(
                slotsMatchingStartAt(refreshed.data?.slots, activeSlot).length > 0
                  ? slotsMatchingStartAt(refreshed.data?.slots, activeSlot)
                  : slotsMatchingStartAt(data?.slots, activeSlot),
                activeSlot,
              );
              const slotsToTry = candidates.length > 0 ? candidates : [activeSlot];
              let lastError: unknown;

              for (const candidate of slotsToTry) {
                try {
                  await mutation.mutateAsync({
                    // The sealed handle is the whole offer — the server reads
                    // the technician and the window out of it, so posting
                    // times here would be both redundant and rejected.
                    slotId: candidate.slotId,
                    ...(mode === 'book' && selectedAddressId
                      ? { addressId: selectedAddressId }
                      : {}),
                  });
                  setSelectedSlot(candidate);
                  toast.success(mode === 'book' ? 'Appointment booked!' : 'Appointment moved!');
                  onDone?.();
                  return;
                } catch (error) {
                  lastError = error;
                }
              }

              toast.error(lastError instanceof Error ? lastError.message : 'Could not book');
            }}
          >
            {mutation.isPending
              ? 'Confirming…'
              : mode === 'book'
                ? 'Confirm booking'
                : 'Confirm new time'}
          </Button>
        </div>
      </Card>
    </PublicLayout>
  );
}

// ─── Shared bits ──────────────────────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">{children}</div>
  );
}

function Brand({ ctx }: { ctx: ScheduleRequestContext }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = Boolean(ctx.branding.logoUrl) && !logoFailed;

  return (
    <div className="mb-5 flex items-center gap-3">
      {showLogo ? (
        <img
          src={ctx.branding.logoUrl}
          alt={ctx.branding.tenantName}
          className="h-10 w-auto"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <div className="text-lg font-semibold tracking-tight text-gray-900">
          {ctx.branding.tenantName}
        </div>
      )}
    </div>
  );
}

function Terminal({
  title,
  body,
  brand,
}: {
  title: string;
  body: string;
  brand?: ScheduleRequestContext;
}) {
  return (
    <PublicLayout maxWidth="sm">
      <Card>
        {brand && <Brand ctx={brand} />}
        <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
        <p className="mt-2 text-sm text-gray-600">{body}</p>
      </Card>
    </PublicLayout>
  );
}

/** A choosable service address. `id === null` means the request's default. */
interface AddressOption {
  id: string | null;
  line: string;
  zip?: string;
  label?: string | null;
}

function normalizeAddr(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function addressOptionFrom(a: PublicBookingAddress): AddressOption {
  return { id: a.id, line: a.line, zip: a.zip, label: a.label };
}

/** Build the choosable address list: the request's default first, then any
 *  other service addresses on file, de-duped against the default (which may
 *  itself be one of them). */
function buildAddressOptions(ctx: ScheduleRequestContext): AddressOption[] {
  const options: AddressOption[] = [{ id: null, line: ctx.address }];
  const defaultNorm = normalizeAddr(ctx.address);
  for (const a of ctx.availableAddresses ?? []) {
    const norm = normalizeAddr(a.line);
    if (norm && defaultNorm.includes(norm)) continue;
    options.push(addressOptionFrom(a));
  }
  return options;
}

function groupByDay(slots: AvailabilitySlot[]): Record<string, AvailabilitySlot[]> {
  const out: Record<string, AvailabilitySlot[]> = {};
  for (const slot of slots) {
    const day = dateKeyInTimeZone(slot.startAt, slot.timezone);
    if (!day) continue;
    (out[day] ??= []).push(slot);
  }
  for (const day of Object.keys(out)) {
    out[day].sort((a, b) => a.startAt.localeCompare(b.startAt));
  }
  return out;
}

/** "9:00 AM – 11:00 AM" style range, rendered in the appointment's timezone
 *  (never the viewer's browser tz — that mismatched the start time above). */
function formatRange(startAt: string, endAt: string, tz: string | null | undefined): string {
  return `${timeInTz(startAt, tz)} – ${timeInTz(endAt, tz)}`;
}

/** "~2 hr" / "~90 min" style window hint. */
function formatDuration(minutes: number): string {
  if (!minutes || minutes <= 0) return '';
  if (minutes % 60 === 0) {
    const hrs = minutes / 60;
    return `~${hrs} hr`;
  }
  if (minutes > 60) {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `~${hrs} hr ${mins} min`;
  }
  return `~${minutes} min`;
}
